import React from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

export default function ProblemSolution() {
  const { t } = useLang();
  return (
    <section className="py-20 md:py-28 bg-slate-50" data-testid="problem-solution-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">{t.problem.eyebrow}</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 text-slate-950">{t.problem.title}</h2>
          <p className="text-slate-600 mt-4 text-base md:text-lg">{t.problem.subtitle}</p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-5 md:gap-6">
          {/* Old way */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl bg-white border border-slate-200 p-7 md:p-9 overflow-hidden"
            data-testid="old-way-card"
          >
            <div className="absolute top-0 right-0 w-40 h-40 bg-rose-100 rounded-full blur-3xl opacity-60 -translate-y-10 translate-x-10" />
            <div className="relative">
              <div className="inline-flex px-3 py-1 rounded-full bg-rose-50 text-rose-600 text-xs font-bold uppercase tracking-wider">
                {t.problem.oldTitle}
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold mt-4 text-slate-700 line-through decoration-rose-300">
                Slow. Expensive. Confusing.
              </h3>
              <ul className="mt-6 space-y-3">
                {t.problem.old.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                      <X className="w-3 h-3 text-rose-600" strokeWidth={3} />
                    </span>
                    <span className="text-[15px]">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* New way */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white p-7 md:p-9 overflow-hidden border border-white/10"
            data-testid="new-way-card"
          >
            <div className="absolute -top-10 -right-10 w-56 h-56 bg-blue-500/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-12 -left-10 w-56 h-56 bg-violet-500/20 rounded-full blur-3xl" />
            <div className="relative">
              <div className="inline-flex px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-bold uppercase tracking-wider border border-emerald-400/20">
                {t.problem.newTitle}
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold mt-4">
                Fast. Affordable. <span className="text-gradient">Built for you.</span>
              </h3>
              <ul className="mt-6 space-y-3">
                {t.problem.new.map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-300" strokeWidth={3} />
                    </span>
                    <span className="text-[15px] text-slate-100">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
